import React from "react"
import { css, type Theme } from "@emotion/react"
import { Link as RRLink, NavLink } from "react-router"
import {
  InternalAnchor,
  InternalButton,
  InternalLink,
  InternalNavLink,
} from "@myvp/shared/src/components/buttons/button-utils"

interface ExternalLinkProps extends React.ComponentProps<"button"> {
  internalType: "button"
}
export interface ExternalNavLinkProps extends React.ComponentProps<
  typeof NavLink
> {
  internalType: "navlink"
}
interface ExternalAnchorProps extends React.ComponentProps<"a"> {
  internalType: "anchor"
}
interface ExternalLinkButtonProps extends React.ComponentProps<typeof RRLink> {
  internalType?: "link"
}

export const sharedLinkStyles = (theme: Theme) => css`
  color: ${theme.palette.content.link.default};
  cursor: pointer;
  text-decoration: none;

  :hover {
    text-decoration: underline;
  }

  :active {
    color: ${theme.palette.content.link.pressed};
    text-decoration: underline;
  }

  :disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    color: ${theme.palette.content.link.disabled};
    text-decoration: none;
  }
`

const baseStyles = (theme: Theme) => css`
  ${sharedLinkStyles(theme)}
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
`

const Link = (
  props:
    | ExternalLinkProps
    | ExternalNavLinkProps
    | ExternalLinkButtonProps
    | ExternalAnchorProps
) => {
  if (props.internalType === "button") {
    const { internalType, ...buttonProps } = props
    return (
      <InternalButton {...buttonProps} css={baseStyles}>
        {props.children}
      </InternalButton>
    )
  } else if (props.internalType === "navlink") {
    const { internalType, ...linkProps } = props
    return (
      <InternalNavLink {...linkProps} css={baseStyles}>
        {props.children}
      </InternalNavLink>
    )
  } else if (props.internalType === "anchor") {
    const { internalType, ...linkProps } = props as ExternalAnchorProps
    return (
      <InternalAnchor {...linkProps} css={baseStyles}>
        {props.children}
      </InternalAnchor>
    )
  } else {
    const { internalType, ...linkProps } = props
    return (
      <InternalLink {...linkProps} css={baseStyles}>
        {props.children}
      </InternalLink>
    )
  }
}

export default Link
